import React, { useState, useRef, useCallback, useMemo } from 'react';
import type { AnnotationTool } from '../types';
import type { CollapsibleToolbarItem } from '../hooks/useResponsiveToolbar';
import { Menu } from '../../components/Menu';
import type { MenuItem, MenuItemSubmenu, MenuItemAction, MenuItemCustom } from '../../components/Menu/types';
import { ANNOTATION_COLORS } from './ColorPicker';

const TOOL_LABELS: Record<AnnotationTool, string> = {
  box: 'Rectangle',
  circle: 'Ellipse',
  line: 'Line',
  arrow: 'Arrow',
  freeform: 'Freeform',
  text: 'Text',
  eraser: 'Eraser',
};

const DRAWING_TOOLS: AnnotationTool[] = ['box', 'circle', 'line', 'arrow', 'freeform', 'text'];

interface ToolbarOverflowMenuProps {
  overflowed: Set<CollapsibleToolbarItem>;
  activeTool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
  activeColor: string;
  onColorChange: (color: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export const ToolbarOverflowMenu = React.memo(function ToolbarOverflowMenu({
  overflowed,
  activeTool,
  onToolChange,
  activeColor,
  onColorChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: ToolbarOverflowMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const items: MenuItem[] = useMemo(() => {
    const result: MenuItem[] = [];

    if (overflowed.has('tool')) {
      const toolChildren = DRAWING_TOOLS.map((tool) => ({
        id: `tool-${tool}`,
        type: 'radio' as const,
        label: TOOL_LABELS[tool],
        checked: tool === activeTool,
        onClick: () => {
          onToolChange(tool);
          setIsOpen(false);
        },
      }));

      result.push({
        id: 'tool-submenu',
        type: 'submenu',
        label: `Tool: ${TOOL_LABELS[activeTool]}`,
        children: toolChildren,
      } satisfies MenuItemSubmenu);
    }

    if (overflowed.has('color')) {
      const colorChildren: MenuItemCustom[] = ANNOTATION_COLORS.map((color) => ({
        id: `color-${color}`,
        type: 'custom' as const,
        render: () => (
          <span
            className={`kollab-annotation-color-option${color === activeColor ? ' kollab-annotation-color-option--active' : ''}`}
            style={{ backgroundColor: color }}
            aria-label={color}
          />
        ),
        onClick: () => {
          onColorChange(color);
          setIsOpen(false);
        },
      }));

      result.push({
        id: 'color-submenu',
        type: 'submenu',
        label: 'Color',
        icon: (
          <span
            className="kollab-annotation-overflow-color-swatch"
            style={{ backgroundColor: activeColor }}
          />
        ),
        children: colorChildren,
        layout: 'grid',
        gridColumns: 5,
      } satisfies MenuItemSubmenu);
    }

    if (overflowed.has('undo')) {
      result.push({
        id: 'undo',
        type: 'action',
        label: 'Undo',
        disabled: !canUndo,
        onClick: () => {
          onUndo();
          setIsOpen(false);
        },
      } satisfies MenuItemAction);
    }

    if (overflowed.has('redo')) {
      result.push({
        id: 'redo',
        type: 'action',
        label: 'Redo',
        disabled: !canRedo,
        onClick: () => {
          onRedo();
          setIsOpen(false);
        },
      } satisfies MenuItemAction);
    }

    return result;
  }, [overflowed, activeTool, activeColor, canUndo, canRedo, onToolChange, onColorChange, onUndo, onRedo]);

  if (overflowed.size === 0) return null;

  return (
    <div className="kollab-annotation-overflow-dropdown" style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        className="kollab-annotation-toolbar-btn"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="More tools"
        aria-expanded={isOpen}
        data-tooltip="More"
        type="button"
      >
        <span className="kollab-annotation-overflow-dots">&#x22EE;</span>
      </button>
      <Menu
        items={items}
        isOpen={isOpen}
        onClose={close}
        ariaLabel="More tools"
        triggerRef={btnRef}
      />
    </div>
  );
});
